import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DAILY_BASE_XP, PHOTO_BONUS_XP, POST_GATE_XP, habits } from '../data/habits';
import { badges } from '../data/levels';
import { currentUser } from '../data/user';
import type { BadgeDef, Habit, HabitId, Level, MoodId, ScoreBand } from '../types';
import { bandForScore, levelForXp, nextLevel } from '../utils/progress';
export type DayState = 'inProgress' | 'firstUse' | 'strongDay' | 'lowEnergy';
export type Celebration = {
  kind: 'dayComplete';
} | {
  kind: 'badge';
  badgeId: string;
  days: number;
};
type CompletedMap = Record<HabitId, boolean>;
interface Snapshot {
  completed: CompletedMap;
  mood: MoodId | null;
  photoBonus: boolean;
  streak: number;
  storedXp: number;
  isFirstUse: boolean;
  /**
   * True only on the calendar day the account was created.
   * REQUIRES BACKEND: derived from the server-side account creation date in
   * the user's timezone, never from the device clock.
   */
  accountCreatedToday: boolean;
  /** The one-time first-day Post has already been published. */
  firstDayPostUsed: boolean;
}
type DaySnapshot = Omit<Snapshot, 'accountCreatedToday' | 'firstDayPostUsed'>;
const empty: CompletedMap = {
  hydration: false,
  movement: false,
  mind: false,
  nutrition: false,
  sleep: false,
  mood: false
};
function snapshotFor(state: DayState): DaySnapshot {
  switch (state) {
    case 'firstUse':
      return {
        completed: {
          ...empty
        },
        mood: null,
        photoBonus: false,
        streak: 0,
        storedXp: 0,
        isFirstUse: true
      };
    case 'strongDay':
      return {
        completed: {
          hydration: true,
          movement: true,
          mind: true,
          nutrition: true,
          sleep: true,
          mood: true
        },
        mood: 'focused',
        photoBonus: false,
        streak: currentUser.streak + 1,
        storedXp: currentUser.totalXp - DAILY_BASE_XP,
        isFirstUse: false
      };
    case 'lowEnergy':
      return {
        completed: {
          ...empty,
          hydration: true,
          mood: true
        },
        mood: 'drained',
        photoBonus: false,
        streak: currentUser.streak,
        storedXp: currentUser.totalXp - 35,
        isFirstUse: false
      };
    default:
      return {
        completed: {
          ...empty,
          hydration: true,
          movement: true,
          nutrition: true
        },
        mood: null,
        photoBonus: false,
        streak: currentUser.streak,
        storedXp: currentUser.totalXp - 50,
        isFirstUse: false
      };
  }
}
interface ProgressValue {
  /** The six base items and their XP. Screens must never restate these. */
  habits: Habit[];
  /** Base Daily Check total. Locked at 100 XP. */
  baseTotalXp: number;
  /** Full Post unlocks at this much XP earned today. */
  postGateXp: number;
  /**
   * Why the full Post is open right now:
   * - 'nectar'   → 50+ earned today (the normal daily rule)
   * - 'firstDay' → one-time activation benefit on the account creation day
   * - null       → locked; Pulse stays available regardless
   */
  postUnlockSource: 'nectar' | 'firstDay' | null;
  /** Call after a full Post is published; spends the first-day benefit if used. */
  consumePostUnlock: () => void;
  /** Photo of the Day bonus, always separate from the base total. */
  photoBonusXp: number;
  xpFor: (id: HabitId) => number;
  pendingHabits: Habit[];
  completed: CompletedMap;
  mood: MoodId | null;
  photoBonus: boolean;
  streak: number;
  isFirstUse: boolean;
  /** Base Daily Check XP earned today. Never exceeds 100. */
  dailyXp: number;
  bonusXp: number;
  completedCount: number;
  remainingXp: number;
  todayScore: number;
  band: ScoreBand;
  level: Level;
  totalXp: number;
  /** Next streak badge still ahead of the user, if any. */
  nextBadge: BadgeDef | null;
  daysToNextBadge: number;
  nextLevel: Level | null;
  xpToNextLevel: number;
  dayComplete: boolean;
  postUnlocked: boolean;
  celebration: Celebration | null;
  toggleHabit: (id: HabitId) => void;
  setMood: (mood: MoodId) => void;
  claimPhotoBonus: () => void;
  dismissCelebration: () => void;
}
const ProgressContext = createContext<ProgressValue | null>(null);
export function ProgressProvider({
  initialState = 'inProgress',
  children
}: {
  initialState?: DayState;
  children: React.ReactNode;
}) {
  const [snapshot, setSnapshot] = useState<Snapshot>(() => ({
    ...snapshotFor(initialState),
    // Prototype: the first-use state represents a brand-new account.
    accountCreatedToday: initialState === 'firstUse',
    firstDayPostUsed: false
  }));
  const [queue, setQueue] = useState<Celebration[]>([]);
  const dailyXp = habits.reduce((acc, habit) => acc + (snapshot.completed[habit.id] ? habit.xp : 0), 0);
  const completedCount = habits.filter(h => snapshot.completed[h.id]).length;
  const dayComplete = completedCount === habits.length;
  const bonusXp = snapshot.photoBonus ? PHOTO_BONUS_XP : 0;
  const applyCompletion = useCallback((nextCompleted: CompletedMap, nextMood: MoodId | null) => {
    const nextCount = habits.filter(h => nextCompleted[h.id]).length;
    const wasComplete = completedCount === habits.length;
    const nowComplete = nextCount === habits.length;
    setSnapshot(prev => {
      let streak = prev.streak;
      if (nowComplete && !wasComplete) streak = prev.streak + 1;
      if (!nowComplete && wasComplete) streak = Math.max(0, prev.streak - 1);
      return {
        ...prev,
        completed: nextCompleted,
        mood: nextMood,
        streak,
        photoBonus: nowComplete ? prev.photoBonus : false
      };
    });
    if (nowComplete && !wasComplete) {
      const streak = snapshot.streak + 1;
      const earned = badges.find(b => b.days === streak);
      const next: Celebration[] = [{
        kind: 'dayComplete'
      }];
      if (earned) {
        next.push({
          kind: 'badge',
          badgeId: earned.id,
          days: earned.days
        });
      }
      setQueue(next);
    }
  }, [completedCount, snapshot.streak]);
  const toggleHabit = useCallback((id: HabitId) => {
    const nextCompleted = {
      ...snapshot.completed,
      [id]: !snapshot.completed[id]
    };
    const nextMood = id === 'mood' && snapshot.completed.mood ? null : snapshot.mood;
    if (id === 'mood' && !snapshot.completed.mood && !snapshot.mood) return;
    applyCompletion(nextCompleted, nextMood);
  }, [applyCompletion, snapshot.completed, snapshot.mood]);
  const setMood = useCallback((mood: MoodId) => {
    const nextCompleted = {
      ...snapshot.completed,
      mood: true
    };
    applyCompletion(nextCompleted, mood);
  }, [applyCompletion, snapshot.completed]);
  const claimPhotoBonus = useCallback(() => {
    setSnapshot(prev => ({
      ...prev,
      photoBonus: true
    }));
  }, []);
  const dismissCelebration = useCallback(() => {
    setQueue(prev => prev.slice(1));
  }, []);

  /*
   * POST GATE — single source of truth.
   * Normal rule: full Post requires POST_GATE_XP earned TODAY.
   * Exception: on the account creation day, ONE full Post is unlocked
   * regardless of Daily Check progress. After that day ends — or once that
   * Post is published — the normal daily rule applies. Pulse is never gated.
   * The gate counts ONLY Daily Check base Bee (`dailyXp`). Bonus Bee — Photo of
   * the Day, Baú da Colmeia or any future bonus — never counts toward it.
   */
  const gateReached = dailyXp >= POST_GATE_XP;
  const firstDayPostAvailable = snapshot.accountCreatedToday && !snapshot.firstDayPostUsed;
  const postUnlockSource: 'nectar' | 'firstDay' | null = gateReached ? 'nectar' : firstDayPostAvailable ? 'firstDay' : null;
  const consumePostUnlock = useCallback(() => {
    if (postUnlockSource === 'firstDay') {
      setSnapshot(prev => ({
        ...prev,
        firstDayPostUsed: true
      }));
    }
  }, [postUnlockSource]);
  const totalXp = snapshot.storedXp + dailyXp + bonusXp;
  const todayScore = Math.min(DAILY_BASE_XP, dailyXp);
  const upcomingBadge = badges.find(b => b.days > snapshot.streak) ?? null;
  const upcomingLevel = nextLevel(totalXp);
  const value = useMemo<ProgressValue>(() => ({
    habits,
    baseTotalXp: DAILY_BASE_XP,
    postGateXp: POST_GATE_XP,
    photoBonusXp: PHOTO_BONUS_XP,
    xpFor: (id: HabitId) => habits.find(h => h.id === id)?.xp ?? 0,
    pendingHabits: habits.filter(h => !snapshot.completed[h.id]),
    completed: snapshot.completed,
    mood: snapshot.mood,
    photoBonus: snapshot.photoBonus,
    streak: snapshot.streak,
    isFirstUse: snapshot.isFirstUse && completedCount === 0,
    dailyXp,
    bonusXp,
    completedCount,
    remainingXp: Math.max(0, DAILY_BASE_XP - dailyXp),
    todayScore,
    band: bandForScore(todayScore),
    level: levelForXp(totalXp),
    totalXp,
    nextBadge: upcomingBadge,
    daysToNextBadge: upcomingBadge ? Math.max(0, upcomingBadge.days - snapshot.streak) : 0,
    nextLevel: upcomingLevel,
    xpToNextLevel: upcomingLevel ? Math.max(0, upcomingLevel.xp - totalXp) : 0,
    dayComplete,
    postUnlocked: postUnlockSource !== null,
    postUnlockSource,
    consumePostUnlock,
    celebration: queue[0] ?? null,
    toggleHabit,
    setMood,
    claimPhotoBonus,
    dismissCelebration
  }), [snapshot, completedCount, dailyXp, bonusXp, todayScore, totalXp, upcomingBadge, upcomingLevel, dayComplete, queue, postUnlockSource, consumePostUnlock, toggleHabit, setMood, claimPhotoBonus, dismissCelebration]);
  return <ProgressContext.Provider value={value} data-id="element-1266" data-mp="1ijwkq7ujiqrh">{children}</ProgressContext.Provider>;
}
export function useProgress(): ProgressValue {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used inside ProgressProvider');
  return ctx;
}